import React, { useEffect, useMemo, useState } from "react";
import PreviewSwapDialog from "./PreviewSwapDialog";
import { PatientsContext } from "../../../../stores/Patients/patients.provider";
import useStore from "../../../../helpers/useStore";
import { observer } from "mobx-react";
import { useApi } from "../../../../api/ApiProvider";
import notificationStore from "../../../../stores/Notification/notificationStore";
import { SeriesSwapAppointmentsDto, SwapValidationErrorDto } from "../../../../api-client";

/**
 * Rx that a single timeSlot (Treatment) bills to.
 */
function rxOfTs(ts: any): string {
  return String(ts?.therapyRx?.rx?.id ?? ts?.therapyRx?.rx?.rxNumber ?? "?");
}

/**
 * Compares before/after at the timeSlot level, matched by timeSlot.id (the only
 * stable key across both payloads). A change exists when a timeSlot appears in
 * BOTH lists with a different Rx. Timeslots missing from "after" (unscheduled /
 * cancelled ones the backend drops) are treated as unchanged. Returns true if no
 * timeSlot's billed prescription changed.
 */
function hasNoChanges(
  allBefore: SeriesSwapAppointmentsDto[],
  allAfter: SeriesSwapAppointmentsDto[]
): boolean {
  if (!allBefore.length && !allAfter.length) return true;
  for (let s = 0; s < allBefore.length; s++) {
    const beforeAppts = ((allBefore[s] as any)?.appointments || []) as any[];
    const afterAppts = ((allAfter[s] as any)?.appointments || []) as any[];

    const afterRxByTsId = new Map<any, string>();
    for (const a of afterAppts) {
      for (const ts of a?.timeSlots || []) {
        if (ts?.id != null) afterRxByTsId.set(ts.id, rxOfTs(ts));
      }
    }

    for (const b of beforeAppts) {
      for (const ts of b?.timeSlots || []) {
        if (ts?.id == null) continue;
        const afterRx = afterRxByTsId.get(ts.id);
        if (afterRx === undefined) continue; // not in "after" -> cannot judge -> unchanged
        if (afterRx !== rxOfTs(ts)) return false;
      }
    }
  }
  return true;
}

const SwapPreFlightDialog: React.FC<{ patientId: number }> = observer(({ patientId }) => {
  const PatientStore = useStore(PatientsContext);
  const { preflightDialogOpen, setPreflightDialogOpen } = PatientStore;
  const { scheduler: schedulerApi } = useApi();

  const [isLoading, setIsLoading] = useState(false);
  const [allBefore, setAllBefore] = useState<SeriesSwapAppointmentsDto[]>([]);
  const [allAfter, setAllAfter] = useState<SeriesSwapAppointmentsDto[]>([]);
  const [initialErrors, setInitialErrors] = useState<SwapValidationErrorDto[]>([]);
  const [initialWarnings, setInitialWarnings] = useState<any[]>([]);

  const handleClose = () => {
    setPreflightDialogOpen(false, []);
    // setAllBefore([]);
    // setAllAfter([]);
  };

  const seriesIds = preflightDialogOpen.seriesIds;

  const noChanges = useMemo(
    () => !isLoading && allBefore.length > 0 && hasNoChanges(allBefore, allAfter),
    [isLoading, allBefore, allAfter]
  );

  // Auto-close silently when triggered automatically and no changes detected
  useEffect(() => {
    if (noChanges && !preflightDialogOpen.manual) {
      handleClose();
    }
  }, [noChanges, preflightDialogOpen.manual]);

  useEffect(() => {
    if (!preflightDialogOpen.open || seriesIds.length === 0) return;

    let cancelled = false;
    const fetchPreview = async () => {
      setIsLoading(true);
      try {
        // Single endpoint now returns both visual preview AND errors
        const swapResult = await schedulerApi.schedulerSimpleSwapAppointments({ 
          seriesIds, 
          previewMode: true 
        });

        if (cancelled) return;

        // Visual preview data
        setAllBefore((swapResult as any)?.beforeSwap || []);
        setAllAfter((swapResult as any)?.afterSwap || []);

        // Errors from simpleSwap
        const allErrors: SwapValidationErrorDto[] = [
          ...((swapResult as any)?.computeErrors?.map((e: any) => ({
            rxId: e.rxId || '',
            rxNumber: e.rxNumber || '',
            label: e.type || '',
            message: e.message || '',
          })) || []),
          ...((swapResult as any)?.validationErrors?.map((e: any) => ({
            rxId: e.rxId || '',
            rxNumber: e.rxNumber || '',
            label: e.type || '',
            message: e.message || '',
          })) || []),
        ];
        setInitialErrors(allErrors);
        setInitialWarnings((swapResult as any)?.warnings || []);
      } catch (error) {
        if (cancelled) return;
        const msg = (error as any)?.response?.data?.message || (error as any)?.message || "";
        notificationStore.showMessage(
          `Vorschau der Tauschtermine konnte nicht geladen werden${msg ? `: ${msg}` : ""}`,
          "error",
          error
        );
        handleClose();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchPreview();
    return () => { cancelled = true; };
  }, [preflightDialogOpen.open, seriesIds]);

  // For auto-triggered flows: don't open the dialog at all until we know
  // there are actual changes. Only show while loading or when changes exist
  // for manual flows (NEU ORDNEN button).
  const dialogOpen =
    preflightDialogOpen.open &&
    (preflightDialogOpen.manual || (!isLoading && !noChanges));

  return (
    <PreviewSwapDialog
      open={dialogOpen}
      onClose={handleClose}
      allBefore={allBefore}
      allAfter={allAfter}
      seriesIds={seriesIds}
      patientId={patientId}
      isLoading={isLoading}
      noChanges={noChanges}
      initialErrors={initialErrors}
      initialWarnings={initialWarnings}
    />
  );
});

export default SwapPreFlightDialog;
